import { type ActionReducerMapBuilder, type EntityState, isAnyOf } from '@reduxjs/toolkit';
import { isDefined } from '@sgme/fp';

import { cashRepriceQuote, optionRepriceQuote } from '@/store/blotter/workflows/cashRepriceQuote/action';
import { generateCashSpotTile } from '@/store/cash/helpers/tile.ts';
import type { CashById } from '@/store/cash/state.ts';
import { getFlexiFromBlotterEntry } from '@/store/flexi-forward/getFlexiFromBlotterEntry.ts';
import { generateEmptyFlexiForwardTile } from '@/store/flexi-forward/helpers/tile.ts';
import type { FlexiForwardById } from '@/store/flexi-forward/state.ts';
import { getOptionFromBlotterEntry, getProductFromBlotterProduct } from '@/store/option/getOptionFromBlotterEntry.ts';
import type { OptionProductName, OptionState } from '@/store/option/state.ts';
import { defaultOptionUiState, emptyOption } from '@/store/option/utils.ts';
import { generateSwapTile } from '@/store/swap/helpers/tile.ts';
import type { SwapById } from '@/store/swap/state.ts';
import { tilesAdapter } from '@/store/tiles/adapter.ts';
import type { Tile } from '@/store/tiles/types.ts';
import { defaultUserPreferenceData } from '@/store/userPreferences/state.ts';

// ████████╗██╗██╗     ███████╗███████╗    ███████╗██╗     ██╗ ██████╗███████╗
// ╚══██╔══╝██║██║     ██╔════╝██╔════╝    ██╔════╝██║     ██║██╔════╝██╔════╝
//    ██║   ██║██║     █████╗  ███████╗    ███████╗██║     ██║██║     █████╗
//    ██║   ██║██║     ██╔══╝  ╚════██║    ╚════██║██║     ██║██║     ██╔══╝
//    ██║   ██║███████╗███████╗███████║    ███████║███████╗██║╚██████╗███████╗
//    ╚═╝   ╚═╝╚══════╝╚══════╝╚══════╝    ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝
//
export function repriceQuotesTilesReducer(builder: ActionReducerMapBuilder<EntityState<Tile, string>>) {
  builder.addMatcher(
    isAnyOf(cashRepriceQuote, optionRepriceQuote),
    (state, { payload: { productType, currentTabId, tileId } }) => {
      tilesAdapter.addOne(state, {
        tileId,
        tabId: currentTabId,
        productType,
        x: 0,
        y: 0,
        h: 1,
        w: 1,
      });
    },
  );
}

//  ██████╗ █████╗ ███████╗██╗  ██╗    ███████╗██╗     ██╗ ██████╗███████╗
// ██╔════╝██╔══██╗██╔════╝██║  ██║    ██╔════╝██║     ██║██╔════╝██╔════╝
// ██║     ███████║███████╗███████║    ███████╗██║     ██║██║     █████╗
// ██║     ██╔══██║╚════██║██╔══██║    ╚════██║██║     ██║██║     ██╔══╝
// ╚██████╗██║  ██║███████║██║  ██║    ███████║███████╗██║╚██████╗███████╗
//  ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝    ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝
//
export function cashRepriceQuoteCashReducer(builder: ActionReducerMapBuilder<CashById>) {
  builder.addCase(cashRepriceQuote, (state, { payload: { productType, tileId, sessionId } }) => {
    if (isDefined(state[tileId]) || productType !== 'Cash') {
      return state;
    }

    return {
      ...state,
      [tileId]: generateCashSpotTile(undefined, defaultUserPreferenceData, sessionId),
    };
  });
}

//    ███████╗██╗    ██╗ █████╗ ██████╗     ███████╗██╗     ██╗ ██████╗███████╗
//    ██╔════╝██║    ██║██╔══██╗██╔══██╗    ██╔════╝██║     ██║██╔════╝██╔════╝
//    ███████╗██║ █╗ ██║███████║██████╔╝    ███████╗██║     ██║██║     █████╗
//    ╚════██║██║███╗██║██╔══██║██╔═══╝     ╚════██║██║     ██║██║     ██╔══╝
//    ███████║╚███╔███╔╝██║  ██║██║         ███████║███████╗██║╚██████╗███████╗
//    ╚══════╝ ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝         ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝
//
export function cashRepriceQuoteSwapReducer(builder: ActionReducerMapBuilder<SwapById>) {
  builder.addCase(cashRepriceQuote, (state, { payload: { tileId, productType } }) => {
    if (isDefined(state[tileId]) || productType !== 'Swap') {
      return state;
    }

    return {
      ...state,
      [tileId]: generateSwapTile(undefined, defaultUserPreferenceData),
    };
  });
}

export function optionRepriceFromBlotterReducer(builder: ActionReducerMapBuilder<OptionState>) {
  builder.addCase(
    optionRepriceQuote,
    (state, { payload: { productType, tileId, entry, children, userPreferences } }) => {
      if (isDefined(state[tileId]) || productType !== 'Option') {
        return state;
      }
      const { optionExpandGreekAndMkt, optionStrategyGroupLegs } = userPreferences;

      const optionProps = getOptionFromBlotterEntry(tileId, entry, children);
      const productName: OptionProductName = getProductFromBlotterProduct(entry.product);
      const option = emptyOption(tileId, productName, optionProps);

      const legs = option.properties.legs;
      const unlinkedCurrencies = legs.some(
        ({ notionalCurrencyIndex }) => notionalCurrencyIndex !== legs[0]?.notionalCurrencyIndex,
      );
      const strategyPanelMode = option.properties.legs.length > 1 && productName === 'Vanilla' ? 'vertical' : 'hidden';
      option.properties.uiState = {
        ...defaultOptionUiState,
        legsNotionalCurrenciesLinkState: unlinkedCurrencies ? 'unlinked' : 'linked',
        strategyPanelMode: strategyPanelMode,
        groupLegsByExpiry: optionStrategyGroupLegs,
        expandGreekAndMarketPanels: optionExpandGreekAndMkt,
      };
      return {
        ...state,
        [tileId]: option,
      };
    },
  );
}

export function flexiRepriceFromBlotterReducer(builder: ActionReducerMapBuilder<FlexiForwardById>) {
  builder.addCase(optionRepriceQuote, (state, { payload: { productType, tileId, entry } }) => {
    if (isDefined(state[tileId]) || productType !== 'FlexiForward') {
      return state;
    }

    const flexiProps = getFlexiFromBlotterEntry(entry);
    const option = generateEmptyFlexiForwardTile(tileId, flexiProps);

    return {
      ...state,
      [tileId]: option,
    };
  });
}
